/* eslint-disable no-alert */
// @flow
import React, { Fragment } from 'react';
import { Link, Route } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Head } from '@finect/tabular-components/Head';
import { Editor, convertToRaw, convertFromRaw } from 'draft-js';
import { EntityCard } from '@finect/tabular-components/EntityCard';
import { Paragraph } from '@finect/tabular-core/Paragraph';
import { H1, H3 } from '@finect/tabular-core/Headings';
import { Container } from '@finect/tabular-core/Container';
import { ContainerInner } from '@finect/tabular-core/ContainerInner';
import { MainContent } from '@finect/tabular-core/MainContent';
import { BottomAds, Ads } from '@finect/tabular-components/Ads';
import { Side } from '@finect/tabular-core/Side';
import { NotFound } from '@finect/tabular-components/NotFound';
import { INDEX_QUESTIONS } from '@finect/tabular-components/Questions';
import { EditButton, DeleteButton, SaveButton, CancelButton } from '@finect/tabular-components/EditionActions';
import { EntryEditor } from '@finect/tabular-draft-editor';
import { slugify } from '@finect/front-utils/slugify';
import { LiveBanner } from '@finect/tabular-components/LiveBanner';
import { dataLayer, Datalayer } from '@finect/front-gtm';
import { withSubscriptions } from '@finect/front-core/User';
import type { WithSubscriptionsProps } from '@finect/front-core/User';
import type { QuestionType, UserType, TagType } from '../../types';

import {
  Disclaimer,
  NotLoggedIn,
  Author,
  AskQuestion,
  EntryHeading,
  EntryContent,
  EntryMeta,
  EntryPublication,
  EntryExtras,
  PostEntry,
  Comments,
  Navigation,
  QuestionDetail,
  QuestionDetailInner,
  QuestionBlock,
  QuestionTitle,
  TitleInput,
  Answer,
  MentionedProducts,
  TagAutocomplete,
  Tags,
  Tag,
  Heading,
  RelatedQuestions,
  Toolbar,
  PendingModerationAlert
} from '../../components';

import {
  getQuestion,
  getQuestionProducts,
  editQuestion,
  deleteQuestion,
  voteQuestion,
  voteAnswer,
  preFetchedQuestions
} from '../../store/questions/actions';

import { sendAnswer, editAnswer, deleteAnswer, selectAnswer } from '../../store/answers/actions';

import { sendComment, deleteComment, sendAnswerComment, deleteAnswerComment } from '../../store/comments/actions';
import { openOverlay } from '../../store/overlay/actions';

const mapStateToProps = (state: Object) => ({
  user: state.user.user,
  questions: state.questions,
  // savedQuestions: state.user.subscriptions.questions,
  likedQuestions: state.user.likes.questions,
  likedAnswers: state.user.likes.answers,
  followedTags: state.user.subscriptions.tags
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      getQuestion,
      getQuestionProducts,
      editQuestion,
      deleteQuestion,
      voteQuestion,
      sendAnswer,
      voteAnswer,
      editAnswer,
      deleteAnswer,
      selectAnswer,
      sendComment,
      deleteComment,
      sendAnswerComment,
      deleteAnswerComment,
      preFetchedQuestions,
      openOverlay
    },
    dispatch
  )
});

type Props = {
  user: UserType,
  history: Object,
  match: Object,
  location: Object,
  actions: Object,
  questions: {
    items: { [key: string]: QuestionType },
    list: Array<string>
  },
  // savedQuestions: Array<string>,
  likedQuestions: Array<string>,
  likedAnswers: Array<string>,
  followedTags: Array<string>
} & WithSubscriptionsProps;

type State = {
  hasValidTitle: boolean,
  isEditing: boolean,
  forceCancel: boolean,
  selectedProducts: Array<Object>,
  selectedTags: Array<TagType>,
  fetchedProducts: boolean
};

class Question extends React.Component<Props, State> {
  titleEditor: Editor;
  questionEditor: Editor;
  answerEditor: Editor;
  // TODO Change any for HTMLElement, and fix Flow...
  answerPostTitle: any;
  selectedAnswer: HTMLElement;
  anchor: boolean;
  dataLayerLastSent: number | string = 0;

  state = {
    hasValidTitle: true,
    isEditing: false,
    forceCancel: false,
    selectedProducts: [],
    selectedTags: [],
    fetchedProducts: false
  };

  static loadInitialData(store, cookie, match) {
    return store.dispatch(getQuestion(match.params.questionId, cookie));
  }

  pushDatalayer = (question) => {
    if (question && question.answers && question.id !== this.dataLayerLastSent) {
      this.dataLayerLastSent = question.id;

      dataLayer({
        id: 'Question',
        type: 'view',
        params: {
          question: {
            ...question,
            answers: question.answers.map(answer => ({
              ...answer,
              author: question.authors[answer.author]
            }))
          }
        }
      });
    }
  };

  componentDidMount() {
    const { actions, match, questions, location } = this.props;
    const { questionId } = match.params;

    const question = questions.items[questionId];
    if (!question || !question.answers || !question.comments) {
      actions.getQuestion(questionId);
      this.anchor = false;
    } else {
      const selectedAnswer = (question.answers || []).find(quest => quest.selected || false) || {};
      if (this.selectedAnswer && location.hash === `#respuesta-${selectedAnswer.id || ''}`) {
        setTimeout(() => window.scrollTo(0, this.selectedAnswer.offsetTop), 0);
      }
      this.anchor = true;
    }

    if (question) {
      this.pushDatalayer(question);
    }
  }

  /* eslint-disable */
  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { questionId } = nextProps.match.params;
    if (this.props.match.params.questionId !== questionId) {
      this.props.actions.getQuestion(questionId);
    }

    const { location, questions } = this.props;
    const question = questions.items[questionId];

    this.pushDatalayer(question);

    if (location.hash && !this.anchor && question.answers) {
      setTimeout(() => window.scrollTo(0, this.selectedAnswer.offsetTop), 0);
      this.anchor = true;
    }
  }
  /* eslint-enable */

  focusInput = (event: SyntheticMouseEvent<HTMLElement>) => {
    event.preventDefault();
    window.scrollTo(0, this.answerPostTitle.offsetTop);
    this.focusAnswerEditor();
  };

  focusAnswerEditor = () => {
    this.answerEditor && this.answerEditor.focus();
  };

  focusTitleEditor = () => {
    this.titleEditor && this.titleEditor.focus();
  };

  focusEditor = () => {
    this.questionEditor && this.questionEditor.focus();
  };

  handleReturn = () => {
    this.focusEditor();
  };

  handleBackspace = () => {
    const content = this.questionEditor.props.editorState.getCurrentContent();
    if (!content.hasText()) {
      // It would also be nice to position the caret at the end
      this.focusTitleEditor();
    }
  };

  handleChange = (content: string) => {
    const validContent = Boolean(content && content.length <= 120);
    const { hasValidTitle } = this.state;
    if (validContent !== hasValidTitle) {
      this.setState({ hasValidTitle: validContent });
    }
  };

  toggleEditing = () => {
    let selectedProducts;
    let selectedTags;

    if (!this.state.isEditing) {
      setTimeout(() => this.focusTitleEditor(), 0);
      const { questionId } = this.props.match.params;
      const question: Object = this.props.questions.items[questionId] || {};
      selectedProducts = [...question.products];
      selectedTags = [...question.tags];
    } else {
      selectedProducts = this.state.selectedProducts;
      selectedTags = this.state.selectedTags;
    }

    this.setState(state => ({
      isEditing: !state.isEditing,
      forceCancel: false,
      selectedProducts,
      selectedTags
    }));
  };

  cancelEdition = () => {
    const { questionId } = this.props.match.params;
    const question: Object = this.props.questions.items[questionId] || {};
    const selectedProducts = question.products;
    const selectedTags = question.tags;

    this.setState({
      isEditing: false,
      forceCancel: true,
      selectedProducts,
      selectedTags
    });
  };

  editQuestion = () => {
    const title = this.titleEditor.props.editorState.getCurrentContent();
    if (!title.hasText() || title.getPlainText().length > 120) {
      return this.cancelEdition();
    }
    this.toggleEditing();
    const content = this.questionEditor.props.editorState.getCurrentContent();
    const { questionId } = this.props.match.params;
    const rawContent = convertToRaw(content);

    const editedQuestion = {
      title: title.getPlainText(),
      rawContent,
      plainText: content.getPlainText(),
      tags: this.state.selectedTags.map(tag => tag.id).join(', '),
      products: this.state.selectedProducts
    };

    return this.props.actions.editQuestion(questionId, editedQuestion);
  };

  deleteQuestion = () => {
    /* eslint-disable no-alert */
    const confirmation = confirm('¿Estás seguro de que quieres eliminar esta pregunta?');
    /* eslint-enable no-alert */

    if (confirmation) {
      const { questionId } = this.props.match.params;
      const redirect = () => {
        this.props.history.push({
          pathname: '/preguntas',
          state: { forceFetch: true }
        });
      };
      this.props.actions.deleteQuestion(questionId, redirect);
    }
  };

  handleAnswerLike = (answerId: number, questionId: string) => {
    const { handleEntityFollow, likedAnswers = [], user, actions } = this.props;

    if (user.isLoggedIn) {
      // TODO no está actualizando redux likedAnswers, no sé por qué ************
      handleEntityFollow('answer', 'like', answerId, !likedAnswers.includes(answerId));
      return actions.voteAnswer(answerId, !likedAnswers.includes(answerId), questionId);
    }
    const blob = {
      copy: 'Accede a Finect para realizar esta acción.',
      action: 'like',
      target: {
        id: answerId,
        type: 'answer'
      }
    };
    actions.openOverlay(blob);
  };

  handleLike = () => {
    const { handleEntityFollow, likedQuestions, user, actions } = this.props;
    const { questionId } = this.props.match.params;

    if (user.isLoggedIn) {
      handleEntityFollow('question', 'like', questionId, !likedQuestions.includes(questionId));
      return actions.voteQuestion(questionId, !likedQuestions.includes(questionId));
    }
    const blob = {
      copy: 'Accede a Finect para realizar esta acción.',
      action: 'like',
      target: {
        id: questionId,
        type: 'question'
      }
    };
    actions.openOverlay(blob);
  };

  // handleFollow = () => {
  //   const { handleEntityFollow, savedQuestions, user, actions } = this.props;
  //   const { questionId } = this.props.match.params;

  //   if (user.isLoggedIn) {
  //     return handleEntityFollow('question', 'subscribe', questionId, !savedQuestions.includes(questionId));
  //   }
  //   const blob = {
  //     copy: 'Accede a Finect para realizar esta acción.',
  //     action: 'like',
  //     target: {
  //       id: questionId,
  //       type: 'question'
  //     }
  //   };
  //   actions.openOverlay(blob);
  // };

  addProduct = (newProduct: Object) => {
    const newProducts = [...this.state.selectedProducts];
    const pos = newProducts.findIndex((product) => {
      const id = (newProduct.entity && newProduct.entity.id) || newProduct.id;
      return product.entity.id === id;
    });

    if (pos === -1) {
      newProducts.push(newProduct);
      this.setState({ selectedProducts: newProducts });
    }
  };

  removeProduct = (products: Array<Object>) => {
    const { selectedProducts } = this.state;

    // Nothing to delete
    if (!selectedProducts.length) return;
    if (products.length === selectedProducts.length) return;

    // All previous products have been removed
    if (!products.length) {
      this.setState({ selectedProducts: [] });
    }

    // Remove deleted products
    const newProducts = selectedProducts.filter(product => products.includes(product.entity.id));
    this.setState({ selectedProducts: newProducts });
  };

  addTag = (tag: TagType) => {
    const newTags = this.state.selectedTags;
    const exists = newTags.some(currentTag => currentTag.id === tag.id);
    if (!exists) {
      newTags.push(tag);
      this.setState({ selectedTags: newTags });
    }
  };

  removeTag = (tagId: number) => {
    const newTags = this.state.selectedTags.filter(tag => tag.id !== tagId);
    this.setState({ selectedTags: newTags });
  };

  removeLatestTag = () => {
    const { selectedTags } = this.state;
    if (selectedTags.length === 0) return;
    const newTags = selectedTags.slice(0, -1);
    this.setState({ selectedTags: newTags });
  };

  addAnswer = (products: Array<Object>) => {
    const { questionId } = this.props.match.params;
    const content = this.answerEditor.props.editorState.getCurrentContent();

    if (!content.hasText()) return;

    const rawContent = convertToRaw(content);
    const plainText = content.getPlainText();

    const newAnswer = {
      rawContent,
      plainText,
      products
    };

    setTimeout(() => {
      localStorage.removeItem(`answerContent-${questionId}`);
    }, 250);

    this.props.actions.sendAnswer(newAnswer, questionId);
  };

  editAnswer = (id: number, editedAnswer: Object) => {
    const { questionId } = this.props.match.params;
    const answer = { id, ...editedAnswer };
    this.props.actions.editAnswer(answer, questionId);
  };

  deleteAnswer = (answerId: number) => {
    const { questionId } = this.props.match.params;
    /* eslint-disable no-alert */
    const confirmation = confirm('¿Estás seguro de que quieres eliminar tu respuesta?');
    /* eslint-enable no-alert */

    if (confirmation) {
      this.props.actions.deleteAnswer(answerId, questionId);
    }
  };

  selectAnswer = (answerId: number) => {
    const { questionId } = this.props.match.params;
    this.props.actions.selectAnswer(answerId, questionId);
  };

  addComment = (comment: Object) => {
    const { questionId } = this.props.match.params;
    this.props.actions.sendComment(comment, questionId);
  };

  deleteComment = (commentId: number) => {
    const { questionId } = this.props.match.params;
    this.props.actions.deleteComment(commentId, questionId);
  };

  addAnswerComment = (answerId: number, comment: Object) => {
    const { questionId } = this.props.match.params;
    this.props.actions.sendAnswerComment(comment, questionId, answerId);
  };

  deleteAnswerComment = (answerId: number, commentId: number) => {
    const { questionId } = this.props.match.params;
    this.props.actions.deleteAnswerComment(commentId, questionId, answerId);
  };

  goBack = () => {
    this.props.history.goBack();
  };

  render() {
    const {
      user,
      match,
      history,
      actions,
      questions,
      likedQuestions = [],
      // savedQuestions = [],
      likedAnswers = [],
      followedTags = []
    } = this.props;
    const { questionId } = match.params;

    // Check if 404 question not found
    if (!questions.items.hasOwnProperty(questionId)) {
      return (
        <Route
          render={props => (
            <NotFound
              message="Lo sentimos pero parece que esta pregunta no existe."
              actionText="Ir a preguntas"
              redirectPath="/preguntas"
              {...props}
            />
          )}
        />
      );
    }
    const { isEditing, forceCancel, hasValidTitle, fetchedProducts } = this.state;
    const question: Object = questions.items[questionId] || {}; // eslint-disable-line
    const isAuthor = (user.id && user.id === question.author) || false;
    const questionPath = `/preguntas/${question.id}/${slugify(question.title)}`;
    const questionURL = `https://${process.env.FINECT_BASEURL || 'www.finect.com'}${questionPath}`;
    const { fromApp, backText } = history.location.state || {};
    const questionTags = isEditing ? this.state.selectedTags : question.tags || [];
    let desc = '';

    if (!question.products.length && !fetchedProducts) {
      actions.getQuestionProducts(questionId);
      this.setState({ fetchedProducts: true });
    }

    if (question.body) {
      if (question.body.content.includes('blocks')) {
        try {
          const data = JSON.parse(question.body.content);
          const content = convertFromRaw(data).getPlainText();
          desc = content.length > 160 ? `${content.slice(0, 160)}...` : content;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log(err);
        }
      } else {
        desc = question.body.content.slice(0, 160);
      }
    }
    const authors = question.authors;
    const author = authors ? authors[question.author] : null;
    const isUpdated = question.updated && question.updated.date !== question.created;
    const selectedAnswer = (question.answers || []).find(quest => quest.selected || false);

    const getDraftJSPlainText = (body) => {
      let plainText = '';
      if (body.content.includes('blocks')) {
        try {
          const data = JSON.parse(body.content);
          plainText = convertFromRaw(data).getPlainText();
        } catch (err) {
          console.log(err);
        }
      }

      return plainText;
    };

    let schemaOrg = {};

    if (author) {
      schemaOrg = {
        '@context': 'http://schema.org',
        '@type': 'QAPage',
        mainEntity: {
          '@type': 'Question',
          name: question.title,
          upvoteCount: question.stats.answers,
          text: getDraftJSPlainText(question.body),
          dateCreated: question.created,
          author: {
            '@type': 'person',
            name: author.alias
          },
          answerCount: 0,
          url: questionURL,
          suggestedAnswer: []
        }
      };

      if (question.answers) {
        const selectedAnswerAuthor = selectedAnswer && authors ? authors[selectedAnswer.author] : null;

        schemaOrg.mainEntity.answerCount = question.answers.length;

        if (selectedAnswer && selectedAnswerAuthor) {
          // $FlowFixMe
          schemaOrg.mainEntity.acceptedAnswer = {
            '@type': 'Answer',
            upvoteCount: selectedAnswer.stats.votes,
            text: getDraftJSPlainText(selectedAnswer.body),
            dateCreated: selectedAnswer.updated.date,
            author: {
              '@type': 'Person',
              name: selectedAnswerAuthor.alias
            },
            url: `${questionURL}#respuesta-${selectedAnswer.id}`
          };
        }

        schemaOrg.mainEntity.suggestedAnswer = question.answers.map(answer => ({
          '@type': 'Answer',
          upvoteCount: answer.stats.votes,
          text: getDraftJSPlainText(answer.body),
          dateCreated: answer.updated.date,
          author: {
            '@type': 'Person',
            name: authors[answer.author].alias
          },
          url: `${questionURL}#respuesta-${answer.id}`
        }));
      }
    }

    return (
      <Container>
        <Head
          title={`${question.title}${selectedAnswer ? ' (Resuelto)' : ''} - Preguntas Finect`}
          description={desc || question.title}
          canonical={questionURL}
          robots={INDEX_QUESTIONS.includes(Number(question.id)) ? 'index, follow' : 'noindex, nofollow'}
        >
          <meta property="og:site_name" content="Finect" />
          <meta property="og:url" content={questionURL} />
          <meta property="og:title" content={`Pregunta: ${question.title}`} />
          <meta property="og:description" content={desc || ''} />
          <meta property="og:image" content="https://static.finect.com/img/banners/sabes-la-respuesta.jpg" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" property="og:title" itemProp="name" content={`Pregunta: ${question.title}`} />
          <meta name="twitter:description" property="og:description" itemProp="description" content={desc || ''} />
          <meta name="twitter:image" content="https://static.finect.com/img/banners/sabes-la-respuesta.jpg" />

          <script type="application/ld+json">{schemaOrg?.mainEntity ? JSON.stringify(schemaOrg) : ''}</script>
        </Head>

        <Datalayer id="Scene" type="ready" params={{ pageType: 'question', pageId: question.id }} />
        <Datalayer id="Scene" type="pageType" params={{ pageType: 'question', pageId: question.id }} />

        <LiveBanner />

        <Heading>
          <Navigation fromApp={fromApp} text={backText} goBack={this.goBack}>
            <AskQuestion secondary variant="small" as={Link} to="/preguntas/nueva-pregunta" rel="nofollow">
              <span>Haz una </span>pregunta
            </AskQuestion>
          </Navigation>
        </Heading>

        <ContainerInner>
          <MainContent>
            {!question.moderated ? <PendingModerationAlert type="question" /> : null}

            <QuestionDetail id="pregunta">
              <QuestionDetailInner>
                <EntryHeading main>
                  {author ? (
                    <Author
                      main
                      author={author}
                      action="pregunta"
                      isUser={user.isLoggedIn && isAuthor}
                      loggedIn={user.isLoggedIn}
                    />
                  ) : null}
                </EntryHeading>
                <EntryContent main isEditable={isAuthor}>
                  <QuestionTitle>
                    {question.title ?
                      isAuthor ? (
                        <TitleInput as={'div'}>
                          <EntryEditor
                            minimal
                            inputRef={title => (this.titleEditor = title)}
                            content={{ type: 'plain', body: question.title }}
                            placeholder="Escribe aquí tu pregunta..."
                            focusEditor={this.focusTitleEditor}
                            forceEdition={{ isEditing, forceCancel }}
                            handleReturn={this.handleReturn}
                            handleChange={this.handleChange}
                            decorators={['limit']}
                            limit={{ chars: 120, extraPadding: 3 }}
                            readOnly={true}
                          />
                        </TitleInput>
                      ) : (
                        <H1>
                          {question.title && question.title.length > 120
                            ? question.title.slice(0, 120)
                            : question.title}
                        </H1>
                      )
                      : null}
                  </QuestionTitle>

                  {question.body && question.body.content && question.body.content.includes('blocks') ? (
                    <EntryEditor
                      inputRef={el => (this.questionEditor = el)}
                      content={{
                        type: 'blocks',
                        body: question.body.content
                      }}
                      handleBackspace={this.handleBackspace}
                      handleAddProduct={this.addProduct}
                      handleRemoveProduct={this.removeProduct}
                      focusEditor={this.focusEditor}
                      forceEdition={{ isEditing, forceCancel }}
                      placeholder={this.state.isEditing ? 'Detalla tu pregunta para que te puedan ayudar...' : ''}
                      decorators={['mentions', 'products', 'link', 'inlink', 'images']}
                      readOnly
                    />
                  ) : (
                    <Fragment>
                      {question.body && question.body.content ? (
                        <Paragraph hero>{question.body.content}</Paragraph>
                      ) : null}
                    </Fragment>
                  )}

                  {isAuthor ?
                    !isEditing ? (
                      <Fragment>
                        <EditButton handleClick={this.toggleEditing} />
                        <DeleteButton handleClick={this.deleteQuestion} />
                      </Fragment>
                    ) : (
                      <Fragment>
                        <SaveButton
                          handleClick={
                            hasValidTitle
                              ? this.editQuestion
                              : () => {
                                alert('Parece que tu pregunta es demasiado larga, revísala para poder guardarla.');
                              }
                          }
                        />
                        <CancelButton handleClick={this.cancelEdition} />
                      </Fragment>
                    )
                    : null}
                </EntryContent>

                <EntryMeta>
                  <div>
                    {question.created ? (
                      <EntryPublication
                        publishDate={question.created}
                        lastRevision={isUpdated ? question.updated.date : null}
                        anchor=""
                      />
                    ) : null}
                  </div>
                  {isEditing ? (
                    <TagAutocomplete
                      selectedTags={this.state.selectedTags}
                      addTag={this.addTag}
                      removeTag={this.removeTag}
                      removeLatestTag={this.removeLatestTag}
                    />
                  ) : questionTags.length ? (
                    <Tags>
                      {questionTags.map(tag => (
                        <EntityCard
                          key={tag.id}
                          type="tag"
                          entity={{
                            id: tag.id,
                            name: tag.name,
                            alias: slugify(tag.name)
                          }}
                          loggedIn={user.isLoggedIn}
                        >
                          <Tag followed={followedTags.includes(tag.id)} tag={tag} />
                        </EntityCard>
                      ))}
                    </Tags>
                  ) : null}

                  {isEditing ?
                    this.state.selectedProducts.length ? (
                      <MentionedProducts products={this.state.selectedProducts} />
                    ) : null
                    : question.products.length ? (
                      <MentionedProducts products={question.products} />
                    ) : null}
                </EntryMeta>
                <EntryExtras>
                  <Toolbar
                    like={{
                      qty: question.stats.votes,
                      action: this.handleLike,
                      done: likedQuestions.includes(questionId),
                      disable: isAuthor ? 'No puedes realizar esta acción sobre tus preguntas' : ''
                    }}
                    respond={{
                      qty: question.stats.answers,
                      solved: Boolean(selectedAnswer),
                      disable: isAuthor
                        ? 'No puedes responder a tu propia pregunta.'
                        : question.answered
                          ? 'Ya has respondido a esta pregunta.'
                          : '',
                      action: this.focusInput
                    }}
                    // save={{
                    //   action: this.handleFollow,
                    //   done: savedQuestions.includes(questionId)
                    // }}
                    share={{
                      title: question.title,
                      path: `/preguntas/${question.id}/${slugify(question.title)}`
                    }}
                  />
                </EntryExtras>
                {question.comments ? (
                  <Comments
                    questionURL={questionURL}
                    comments={question.comments}
                    user={user}
                    authors={authors}
                    handleDeleteComment={this.deleteComment}
                    handleAddComment={this.addComment}
                  />
                ) : null}
              </QuestionDetailInner>
            </QuestionDetail>
            <QuestionBlock>
              <div>
                {question.answers
                  ? question.answers.map((answer) => {
                    const answerAuthor = authors[answer.author];
                    const isSelected = Boolean(answer.selected);
                    return (
                      <Answer
                        key={answer.id}
                        answerRef={isSelected ? el => (this.selectedAnswer = el) : null}
                        questionTitle={question.title}
                        questionPath={questionPath}
                        answer={answer}
                        questionId={questionId}
                        isSelected={isSelected}
                        isQuestionAuthor={isAuthor}
                        isAuthor={answerAuthor.id === user.id}
                        blockVote={answerAuthor.id === user.id}
                        voted={likedAnswers.includes(answer.id)}
                        handleLike={this.handleAnswerLike}
                        editAnswer={this.editAnswer}
                        deleteAnswer={this.deleteAnswer}
                        selectAnswer={this.selectAnswer}
                        addComment={this.addAnswerComment}
                        deleteComment={this.deleteAnswerComment}
                        user={this.props.user}
                        authors={authors}
                        author={answerAuthor}
                      />
                    );
                  })
                  : null}
                {!isAuthor && !question.answered && question.id ? (
                  <Fragment>
                    <H3 ref={el => (this.answerPostTitle = el)}>¿Sabes la respuesta?</H3>
                    {!user.isLoggedIn ? (
                      <NotLoggedIn url={questionURL} msg="responder a esta pregunta" />
                    ) : (
                      <PostEntry
                        id="responder"
                        name={`answerContent-${question.id}`}
                        placeholder={`Ayuda a @${
                          author ? author.alias : ''
                        } y a otros usuarios de la comunidad respondiendo a sus dudas`}
                        user={this.props.user}
                        inputRef={el => (this.answerEditor = el)}
                        addAnswer={this.addAnswer}
                        focusEditor={this.focusAnswerEditor}
                        minHeight={{ size: 136, apply: 'all' }}
                      />
                    )}
                  </Fragment>
                ) : null}
              </div>
            </QuestionBlock>

            <Disclaimer />
          </MainContent>
          <Side>
            <div>
              <Ads id="Finect_Side2" />
            </div>

            {question.id && question.tags[0] ? (
              <RelatedQuestions
                title="Otras preguntas"
                hide="miniDesktop"
                ignoreId={question.id}
                tag={question.tags[0]}
                register={actions.preFetchedQuestions}
              />
            ) : null}
          </Side>
        </ContainerInner>

        <BottomAds path="Finect_Content_1" sticky closeable />
      </Container>
    );
  }
}

export default withSubscriptions(connect(mapStateToProps, mapDispatchToProps)(Question), ['profiles', 'products']);
